/** Cliente HTTP con sesión en cookies y protección CSRF. */
import axios, { AxiosError, type AxiosInstance, type InternalAxiosRequestConfig } from 'axios';
import { isProtectedPath } from '@/config/authPaths';

const BASE_URL = import.meta.env.VITE_API_URL ?? '';
const CSRF_COOKIE = 'erpa_csrf';

function csrfToken(): string | null {
  const entry = document.cookie.split('; ').find((part) => part.startsWith(`${CSRF_COOKIE}=`));
  return entry ? decodeURIComponent(entry.slice(CSRF_COOKIE.length + 1)) : null;
}

function addCsrf(config: InternalAxiosRequestConfig): InternalAxiosRequestConfig {
  const method = config.method?.toUpperCase() ?? 'GET';
  if (!['GET', 'HEAD', 'OPTIONS'].includes(method)) {
    const token = csrfToken();
    if (token) config.headers.set('X-CSRF-Token', token);
  }
  return config;
}

export const apiClient: AxiosInstance = axios.create({
  baseURL: BASE_URL,
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' },
  timeout: 30_000,
});

apiClient.interceptors.request.use(addCsrf);

let refreshPromise: Promise<void> | null = null;

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const request = error.config as (InternalAxiosRequestConfig & { _retry?: boolean }) | undefined;
    const path = request?.url ?? '';
    const isAuthAction = /\/api\/v1\/auth\/(login|refresh|logout|forgot-password|reset-password)$/.test(path);
    if (error.response?.status !== 401 || !request || request._retry || isAuthAction) {
      return Promise.reject(error);
    }

    request._retry = true;
    try {
      refreshPromise ??= apiClient.post('/api/v1/auth/refresh').then(() => undefined).finally(() => {
        refreshPromise = null;
      });
      await refreshPromise;
      return apiClient(request);
    } catch (refreshError) {
      // El reintento de refresh se hace en todas partes: una cookie de refresco
      // viva debe poder revivir la sesión sin importar en qué pantalla esté el
      // operador. Lo que se acota es el **redirect**, no la recuperación.
      //
      // Solo se expulsa a `/login` cuando la ruta actual exige sesión, porque ese
      // redirect responde a "tu sesión venció mientras operabas". En una pantalla
      // de acceso un 401 es el estado esperado —significa "no estás conectado"— y
      // navegar tiraría al visitante justo cuando está siguiendo el procedimiento
      // correcto. `AuthProvider` consulta `/auth/me` al montar, así que sin este
      // filtro abrir `/set-password#token=…` en un navegador limpio terminaba en
      // la pantalla de login y el enlace de un solo uso se perdía sin usarse.
      // Ver docs/design.md 16.8.1.
      if (isProtectedPath(window.location.pathname)) {
        window.location.href = '/login';
      }
      return Promise.reject(refreshError);
    }
  },
);

export function extractErrorMessage(error: unknown): string {
  if (axios.isAxiosError(error)) {
    const detail = error.response?.data?.detail;
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail) && detail[0]?.msg) return detail[0].msg;
    if (error.response?.status === 413) {
      return 'El archivo supera el tamaño máximo permitido.';
    }
    return error.message;
  }
  if (error instanceof Error) return error.message;
  return 'Error desconocido';
}

/**
 * Código de dominio del error, si el servidor lo envió.
 *
 * Los servicios de credenciales adjuntan un `code` estable a cada rechazo del
 * enlace, para que la interfaz distinga los casos por código y no por el texto.
 * Mapearlos por el `detail` era frágil: cualquier reescritura del copy rompía el
 * mapeo en silencio y terminaba culpando al usuario de algo que no había hecho.
 */
export function extractErrorCode(error: unknown): string | null {
  if (axios.isAxiosError(error)) {
    const code = error.response?.data?.code;
    return typeof code === 'string' ? code : null;
  }
  return null;
}

/**
 * `true` si el servidor rechazó la escritura por conflicto de revisión (409).
 *
 * El backend lanza estos conflictos como `HTTPException(409, "REVISION_CONFLICT: …")`,
 * sin campo `code`: el detalle llega en `detail` y solo `extractErrorCode` vería `null`.
 * Por eso la detección combina el status con el prefijo del detalle, y no solo el
 * `code`. Un 409 exige refrescar la base antes de reintentar; reintentar a ciegas
 * repetiría la intención con la misma clave de idempotencia y devolvería el mismo
 * rechazo sin explicar nada.
 */
export function extractRevisionConflict(error: unknown): boolean {
  if (!axios.isAxiosError(error)) return false;
  if (error.response?.status !== 409) return false;
  const code = error.response?.data?.code;
  if (typeof code === 'string') return code === 'REVISION_CONFLICT';
  const detail = error.response?.data?.detail;
  return typeof detail === 'string' && detail.startsWith('REVISION_CONFLICT');
}

/**
 * Mensaje apto para la persona, sin el prefijo de maquina.
 *
 * Los conflictos llegan como `"REVISION_CONFLICT: revisión desactualizada"`: el
 * token es util para el codigo (`extractRevisionConflict`) y ruido en pantalla.
 * Solo se recorta un prefijo en mayusculas con dos puntos, que es la convencion
 * que usa el backend para el codigo de dominio.
 */
export function extractErrorMessageForUser(error: unknown): string {
  return extractErrorMessage(error)
    .replace(/^[A-Z][A-Z_]*:\s*/, '')
    .replace(/^Value error,\s*/i, '')
    .replace(/new_requirement\.required_date/gi, 'Fecha requerida')
    .replace(/required_date/gi, 'Fecha requerida');
}
